/**
 * WhatsApp two-way conversation view: a contact list on the left and the selected
 * thread (inbound left / outbound right) with a reply box on the right.
 */

import { useEffect, useState } from 'react';
import { IconSearch } from '@tabler/icons-react';
import { Stack, Group, Text, Title, Badge, Button, TextInput, Loader, Alert, ScrollArea, SimpleGrid, SegmentedControl } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import { useWhatsAppConversations, type ConversationSummary } from './useWhatsAppConversations';
import { decodeMessageBody } from './decodeBody';

/**
 * Reduce a phone number (any format) to its local significant digits so search
 * matches regardless of how it was saved: strips a leading +27 / 27 country code
 * or a leading 0 trunk prefix. e.g. 0821234567, +27821234567, 27821234567 → 821234567.
 */
function localDigits(s?: string | null): string {
  let d = (s || '').replace(/\D/g, '');
  if (d.startsWith('27') && d.length > 2) d = d.slice(2);
  else if (d.startsWith('0') && d.length > 1) d = d.slice(1);
  return d;
}

function fmt(ts: number): string {
  if (!ts || ts < 19000101000000) return '';
  const s = String(Math.trunc(ts)).padStart(14, '0');
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}

export function ConversationView() {
  const {
    loadingList, loadingThread, sending, error,
    conversations, thread,
    fetchConversations, fetchThread, reply, markRead,
  } = useWhatsAppConversations();

  const [selected, setSelected] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [filter, setFilter] = useState<'all' | 'replies'>('all');
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const qDigits = localDigits(query);
  const shown = conversations
    .filter((c) => (filter === 'replies' ? c.inboundCount > 0 : true))
    .filter((c) => {
      if (!q) return true;
      if (
        (c.contactName || '').toLowerCase().includes(q)
        || (c.contactNumber || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q)
        || (c.lastMessage || '').toLowerCase().includes(q)
      ) return true;
      // Format-agnostic number search: 0xx / +27xx / 27xx / bare local all match.
      return qDigits.length >= 3 && localDigits(c.contactNumber).includes(qDigits);
    });

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const openThread = (c: ConversationSummary) => {
    setSelected(c.contactNumber);
    fetchThread(c.contactNumber);
    if (c.unreadCount > 0) markRead(c.contactNumber);
  };

  const handleReply = async () => {
    if (!selected || !replyText.trim()) return;
    const ok = await reply(selected, replyText.trim());
    if (ok) setReplyText('');
  };

  if (error) {
    return <Alert color="red" title="Failed to load conversations">{error}</Alert>;
  }

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      {/* Contact list */}
      <Card withBorder padding="sm">
        <Stack gap="xs">
          <Group justify="space-between">
            <Title order={5}>Conversations</Title>
            <Button size="xs" variant="subtle" onClick={fetchConversations} loading={loadingList}>Refresh</Button>
          </Group>
          <TextInput
            size="xs"
            placeholder="Search name, number or message…"
            leftSection={<IconSearch size={14} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <Group justify="space-between" wrap="nowrap">
            <SegmentedControl
              size="xs"
              value={filter}
              onChange={(v) => setFilter(v as 'all' | 'replies')}
              data={[{ label: 'All', value: 'all' }, { label: 'With replies', value: 'replies' }]}
            />
            <Text size="xs" c="dimmed">{shown.length}</Text>
          </Group>
          {loadingList && conversations.length === 0 ? (
            <Group justify="center" p="md"><Loader /></Group>
          ) : shown.length === 0 ? (
            <Text size="sm" c="dimmed" p="sm">
              {conversations.length === 0 ? 'No conversations yet.' : 'No conversations with inbound replies.'}
            </Text>
          ) : (
            <ScrollArea h={480}>
              <Stack gap={4}>
                {shown.map((c) => (
                  <Card
                    key={c.contactNumber}
                    withBorder
                    padding="xs"
                    onClick={() => openThread(c)}
                    style={{ cursor: 'pointer', borderColor: selected === c.contactNumber ? 'var(--mantine-color-teal-5)' : undefined }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" fw={500} truncate>{c.contactName || c.contactNumber}</Text>
                          {c.isMember && <Badge size="xs" variant="light" color="teal" style={{ flexShrink: 0 }}>Member</Badge>}
                        </Group>
                        {c.contactName
                          ? <Text size="9px" c="dimmed" truncate>{c.contactNumber}{c.email ? ` · ${c.email}` : ''}</Text>
                          : (c.email && <Text size="9px" c="dimmed" truncate>{c.email}</Text>)}
                        <Text size="xs" c="dimmed" truncate>{decodeMessageBody(c.lastMessage) || ''}</Text>
                      </Stack>
                      <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                        <Text size="xs" c="dimmed">{fmt(c.lastActivity)}</Text>
                        <Group gap={6} wrap="nowrap">
                          <Text size="9px" c="dimmed">↓{c.inboundCount} ↑{c.outboundCount}</Text>
                          {c.unreadCount > 0 && <Badge size="xs" color="teal" circle>{c.unreadCount}</Badge>}
                        </Group>
                      </Stack>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </Stack>
      </Card>

      {/* Thread */}
      <Card withBorder padding="sm">
        {!selected ? (
          <Group justify="center" p="xl"><Text size="sm" c="dimmed">Select a conversation to view the thread.</Text></Group>
        ) : (
          <Stack gap="sm" style={{ height: '100%' }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Group gap={6} wrap="nowrap">
                  <Title order={5} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thread?.contactName || selected}</Title>
                  {thread?.isMember && <Badge size="xs" variant="light" color="teal" style={{ flexShrink: 0 }}>Member</Badge>}
                </Group>
                {thread?.email && <Text size="xs" c="dimmed" truncate>{thread.email}</Text>}
              </Stack>
              <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{selected}</Text>
            </Group>

            {loadingThread && !thread ? (
              <Group justify="center" p="md"><Loader /></Group>
            ) : (
              <ScrollArea h={440}>
                <Stack gap={6} p="xs">
                  {(thread?.messages ?? []).map((m) => {
                    const outbound = m.direction === 'Outbound';
                    return (
                      <Group key={`${m.direction}-${m.id}`} justify={outbound ? 'flex-end' : 'flex-start'}>
                        <Card
                          withBorder
                          padding="xs"
                          style={{
                            maxWidth: '78%',
                            background: outbound ? 'var(--mantine-color-teal-light)' : 'var(--mantine-color-default)',
                          }}
                        >
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{decodeMessageBody(m.body) || `[${m.type}]`}</Text>
                          <Group gap={6} justify="flex-end" mt={2}>
                            <Text size="9px" c="dimmed">{fmt(m.timestamp)}</Text>
                            {outbound && m.status && <Text size="9px" c="dimmed">· {m.status}</Text>}
                          </Group>
                        </Card>
                      </Group>
                    );
                  })}
                  {(thread?.messages?.length ?? 0) === 0 && (
                    <Text size="sm" c="dimmed" ta="center">No messages.</Text>
                  )}
                </Stack>
              </ScrollArea>
            )}

            {/* Reply box */}
            <Group gap="xs" wrap="nowrap" mt="auto">
              <TextInput
                placeholder="Type a reply… (24h session window)"
                value={replyText}
                onChange={(e) => setReplyText(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                style={{ flex: 1 }}
              />
              <Button onClick={handleReply} loading={sending} disabled={!replyText.trim()}>Send</Button>
            </Group>
          </Stack>
        )}
      </Card>
    </SimpleGrid>
  );
}
